import { CdkVirtualScrollViewport, ScrollingModule, VIRTUAL_SCROLL_STRATEGY } from "@angular/cdk/scrolling";
import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { DynamicSizeVirtualScrollDirective, DynamicSizeVirtualScrollStrategy } from "shared";
import { DATE_SHORT_YEAR_FORMAT, ScrollableItem } from "types";
import { ExecPipe } from "shared-pipes";
import { asyncScheduler, BehaviorSubject, Observable, Subject, Subscription, take, takeUntil } from "rxjs";
import moment, { Moment } from "moment";
import { IDayView } from "types";
import { PLANNING_SCROLLING_BATCH } from "../../../../planning-module/src/lib/constants";

const ITEM_RENDER_DELAY = 600;

@Component({
  selector: "db-dynamic-size-virtual-scroll",
  templateUrl: "./dynamic-size-virtual-scroll.component.html",
  styleUrls: ["./dynamic-size-virtual-scroll.component.scss"],
  imports: [CommonModule, ScrollingModule, DynamicSizeVirtualScrollDirective, ExecPipe],
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useClass: DynamicSizeVirtualScrollStrategy,
    },
  ],
})
export class DynamicSizeVirtualScrollComponent implements AfterViewInit, OnDestroy {
  @Input() scrollableItems$!: Observable<Array<any & ScrollableItem>>;

  @ContentChild("scrollableItemTemplate")
  scrollableItemTemplate!: TemplateRef<any>;

  // Event emitter for when the user scrolls to a point where fetching next data is needed
  @Output() onFetchDataInitiated = new EventEmitter<{
    fetchFromDate: string;
  }>();

  // Event emitter for every scroll event
  @Output() onScrolledElementChange = new EventEmitter<{
    scrollToDate: Moment;
  }>();

  @ViewChild(CdkVirtualScrollViewport) scroller?: CdkVirtualScrollViewport;

  private viScrollSubscriptions = new Subscription();
  private killSubscriptions$$: Subject<void> = new Subject<void>();

  //** Holds the date that we want to fetch data from. On each change, event is emitted to the parent component */
  private fetchDataStartDate$$ = new BehaviorSubject(moment().format(DATE_SHORT_YEAR_FORMAT));

  scrollToDate(date: Moment) {
    this.scrollableItems$.pipe(take(1)).subscribe((days) => {
      const scrollToValue = date.format(DATE_SHORT_YEAR_FORMAT);
      const existingDateIndex = days.findIndex((d) => d.day === scrollToValue);
      if (existingDateIndex >= 0) {
        // if data is loaded, just scroll to it
        // async needed to trigger cd as invoiking this function programmaticly, not by interaction
        asyncScheduler.schedule(() => {
          this.scroller?.scrollToIndex(existingDateIndex, "smooth");
        }, ITEM_RENDER_DELAY);
      } else {
        // if data is not loaded, start loading it and scroll to it (it will be either in loading state (isDummyValue) or already loaded)
        this.fetchDataStartDate$$.next(scrollToValue);
        asyncScheduler.schedule(() => {
          this.scrollToDate(date);
        }, ITEM_RENDER_DELAY);
      }
    });
  }

  scrolledIndexChangeHandler(index: number) {
    this.scrollableItems$.pipe(take(1)).subscribe((scrollableItems) => {
      if (!scrollableItems.length) {
        return;
      }
      const currentIndex = index > 0 ? index : 0;
      const currentItem = scrollableItems[currentIndex];
      if (!currentItem) {
        return;
      }

      /*
      The expected behaviour is to always see the most recent fetched data in the viewport, no matter the scrolling direction.
      To do so, we need to fetch data from the "past", and data in the "future" (relatively to the current scrolled item).
       */

      // range is used for:
      //    - the threshold (which trigger the fetch)
      //    - how many past days (items) we want to fetch data relatively to the selected index
      const range = PLANNING_SCROLLING_BATCH;
      const selectedDate = moment(currentItem.day, DATE_SHORT_YEAR_FORMAT);
      this.onScrolledElementChange.emit({
        scrollToDate: selectedDate,
      });

      if (currentIndex % range === 0) {
        const indexForFetching = Math.max(0, currentIndex - range);
        this.fetchDataStartDate$$.next(scrollableItems[indexForFetching].day);
      }
    });
  }

  trackByFn = (index: number, item: any) => item.day;

  ngAfterViewInit(): void {
    this.fetchDataStartDate$$.pipe(takeUntil(this.killSubscriptions$$)).subscribe((currentFetchStartDate) => {
      this.onFetchDataInitiated.emit({
        fetchFromDate: currentFetchStartDate,
      });
    });
  }

  getHeight(day: IDayView): number {
    return day.additionalOptions?.height
      ? day.additionalOptions.height > (day.height || 0)
        ? day.additionalOptions.height
        : day.height || 0
      : day.height || 0;
  }

  ngOnDestroy(): void {
    this.viScrollSubscriptions?.unsubscribe();
    this.killSubscriptions$$.next();
    this.killSubscriptions$$.complete();
  }
}
